import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useApi = (endpoint, options = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { token } = useAuth();

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const config = {
        ...options,
        headers: {
          ...options.headers,
          ...(token && { Authorization: `Bearer ${token}` })
        }
      };

      const response = await axios.get(`${API_URL}${endpoint}`, config);
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (endpoint) {
      fetchData();
    }
  }, [endpoint, token]);

  const refetch = () => fetchData();

  return { data, loading, error, refetch };
};

export const useApiMutation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { token } = useAuth();

  const mutate = async (method, endpoint, data = null, options = {}) => {
    try {
      setLoading(true);
      setError(null);

      const config = {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
          ...(token && { Authorization: `Bearer ${token}` })
        }
      };

      let response;
      const url = `${API_URL}${endpoint}`;

      switch (method.toLowerCase()) {
        case 'post':
          response = await axios.post(url, data, config);
          break;
        case 'put':
          response = await axios.put(url, data, config);
          break;
        case 'patch':
          response = await axios.patch(url, data, config);
          break;
        case 'delete':
          response = await axios.delete(url, config);
          break;
        default:
          throw new Error(`Unsupported HTTP method: ${method}`);
      }

      return { success: true, data: response.data };
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.response?.data?.message || err.message || 'An error occurred';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  return { mutate, loading, error };
};

// Specific API hooks for common operations
export const useCompanySettings = () => {
  return useApi('/api/admin/company');
};

export const useServices = () => {
  return useApi('/api/admin/services');
};

export const useHeroStats = () => {
  return useApi('/api/admin/hero-stats');
};

export const usePublicData = (endpoint) => {
  return useApi(endpoint);
};